import { state } from "./state";
import { leagues } from "./geometry";
import { chartPoint, clearPreview, showPreview } from "./board";
import { cancelAutoScroll, scrollToVerdict } from "./scroll";
import { svgEl } from "./art";
import { seaEl } from "../dom";
import { recompute, currentRouteLength } from "./flow";

/* ---------- Floaty feedback (SVG text that drifts up and fades) ---------- */
function floaty(text: string, x: number, y: number, type: "gain" | "loss"): void {
  const t = svgEl("text", { class: "floaty " + type, x, y: y - 30, "text-anchor": "middle" });
  t.textContent = text;
  seaEl.appendChild(t);
  setTimeout(() => t.remove(), 1000);
}

/* ---------- Tap = chart / drop a stop ---------- */
function handleTap(idx: number): void {
  const L = state.level();
  const [x, y] = chartPoint(L.points[idx]);

  if (idx === 0) {
    // the harbor is the fixed start; the homeward leg closes automatically
    if (state.route.length < state.portCount()) {
      const msg = document.getElementById("msg");
      if (msg) {
        msg.className = "msg";
        msg.textContent = "The ship sails home by itself once every port has its spice.";
      }
    }
    return;
  }

  const pos = state.route.indexOf(idx);
  if (pos >= 0) {
    // drop the stop; the course reconnects around it (and any pending
    // glide down to the verdict no longer applies)
    cancelAutoScroll();
    const before = currentRouteLength();
    state.route.splice(pos, 1);
    clearPreview();
    recompute();
    const saved = before - currentRouteLength();
    if (saved > 0.05) floaty(`−${leagues(saved)} lg`, x, y, "gain");
  } else {
    const cur = state.route.length ? state.route[state.route.length - 1] : 0;
    const added = state.dist()[cur][idx];
    state.route.push(idx);
    clearPreview();
    recompute();
    floaty(`+${leagues(added)} lg`, x, y, "loss");
    if (state.route.length === state.portCount()) {
      // the round just closed — let the ship sail home, then glide down to
      // the verdict and the Next button (see scroll.ts for the choreography)
      scrollToVerdict();
    }
  }
}

/* ---------- Pointer wiring (event delegation on the SVG) ---------- */
function portIdxFromEvent(e: Event): number | null {
  const target = e.target as Element | null;
  const port = target?.closest?.(".port") as SVGGElement | null;
  if (!port || !seaEl.contains(port)) return null;
  const idx = Number(port.dataset.idx);
  return Number.isInteger(idx) ? idx : null;
}

function onClick(e: Event): void {
  if (state.phase !== "play") return;
  const idx = portIdxFromEvent(e);
  if (idx === null) return;
  e.preventDefault();
  handleTap(idx);
}

function onOver(e: Event): void {
  if (state.phase !== "play") return;
  const idx = portIdxFromEvent(e);
  if (idx === null || idx === 0) return;
  // preview only for ports not yet on the route
  if (state.route.includes(idx)) return;
  showPreview(idx);
}

function onOut(e: Event): void {
  if (portIdxFromEvent(e) !== null) clearPreview();
}

/** Wire up all chart pointer interaction. Call once at init. */
export function initInteraction(): void {
  seaEl.addEventListener("click", onClick);
  seaEl.addEventListener("pointerover", onOver);
  seaEl.addEventListener("pointerout", onOut);
}
